import { TaskParty, TaskStatus } from '@prisma/client';
import { useMemo } from 'react';

import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { AppType, cn, useApp } from '@commenda/utils';
import { ServiceRequestModel } from '@commenda/utils/models/service-request';

import { WorkflowStepDescription } from '../../../../components';
import { WorkflowResetToStepAction } from '../../../common/tasks/workflow/WorkflowResetToStepAction';
import {
  ServiceRequestTaskCardDisplayVariant,
  ServiceRequestTaskProps,
} from '../../../types';
import { ServiceRequestSkipAndUndoEllipsisButton } from '../../tabs/components/timeline/ServiceRequestSkipAndUndoEllipsisButton';
import { ServiceRequestTaskCompletionHelper } from './service-request-task-completion-helper';
import { ServiceRequestCardAvatar } from './ServiceRequestCardAvatar';
import { ServiceRequestTaskCompletion } from './ServiceRequestTaskCompletion';
import { TaskSLABadges } from './TaskSLABadges';

interface ServiceRequestTimelineTaskCardProps {
  task: ServiceRequestTaskProps['task'];
  config: ServiceRequestTaskProps['config'];
  state: ServiceRequestTaskProps['state'];
  context: ServiceRequestTaskProps['context'];
}

export function ServiceRequestTimelineTaskCard({
  task,
  config,
  state,
  context,
}: ServiceRequestTimelineTaskCardProps) {
  const { appType } = useApp();

  const stateConfig = config.getStateConfig(state);
  const statusFlags = ServiceRequestTaskCompletionHelper.getStatusFlags(
    task.status
  );

  const stepDefinition = useMemo(() => {
    if (
      !context.workflowExecution?.WorkflowTemplateVersion?.definition?.steps ||
      !task.workflowDefinitionStepId
    )
      return null;
    return context.workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step: { id: string }) => step.id === task.workflowDefinitionStepId
    );
  }, [context.workflowExecution, task.workflowDefinitionStepId]);

  const isTaskEligibleForReopenOrSkip =
    ServiceRequestModel.isTaskEligibleForReopenOrSkip(
      task,
      context.serviceRequest
    );

  const showTaskSLABadges = [AppType.AGENT, AppType.ADMIN].includes(appType);

  const isComplianceVariant =
    context.variant === ServiceRequestTaskCardDisplayVariant.COMPLIANCE;

  return (
    <div
      className={cn(
        statusFlags.isDisabled && 'cursor-not-allowed',
        isComplianceVariant && 'p-xl'
      )}
    >
      <div className="flex gap-6xl items-start w-full">
        <div className="flex-1 flex flex-col gap-xxs min-w-0">
          <p
            className={cn(
              'txt-medium-bold text-text-primary',
              statusFlags.isDisabled && 'text-text-disabled'
            )}
          >
            {stateConfig.title ?? stateConfig.label ?? task.title}
          </p>
          {stateConfig.description !== undefined ? (
            stateConfig.description
          ) : (
            <WorkflowStepDescription
              description={stepDefinition?.description}
              className="txt-extra-small-regular text-text-secondary"
            />
          )}
        </div>

        <div className="flex items-center gap-md shrink-0">
          {showTaskSLABadges && (
            <TaskSLABadges
              workflowExecution={context.workflowExecution}
              workflowDefinitionStepId={task.workflowDefinitionStepId}
            />
          )}
          <ServiceRequestCardAvatar
            state={state}
            task={task}
            context={context}
          />
          {state.role === TaskParty.AGENT && isTaskEligibleForReopenOrSkip && (
            <ServiceRequestSkipAndUndoEllipsisButton
              serviceRequest={context.serviceRequest}
              task={task}
              mutate={context.mutate}
            />
          )}
        </div>
      </div>

      <div className="mt-xl flex flex-col gap-xs">
        {stateConfig.actions ? stateConfig.actions : null}
        {isFeatureLaunched(FeatureLaunch.WORKFLOW_RESET_TO_STEP_ACTION) && (
          <WorkflowResetToStepAction
            workflowExecutionId={context.workflowExecution?.id}
            targetStepDefinitionId={task.workflowDefinitionStepId}
            role={state.role}
            taskStatus={task.status}
            onResetSuccess={context.mutate}
          />
        )}
        <ServiceRequestTaskCompletion task={task} context={context} />
        {task.status === TaskStatus.COMPLETED &&
          (task.completedAt ?? task.updatedAt) && (
            <p className="txt-extra-small-regular text-text-primary">
              Completed on{' '}
              {ServiceRequestTaskCompletionHelper.formatCompletedDate(
                task.completedAt ?? task.updatedAt
              )}
            </p>
          )}
      </div>
    </div>
  );
}
